import { useState } from 'react';
import { ChevronDown, ChevronUp, FileText, Database, Link2, FileQuestion } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { Citation } from '@/types/api.types';

export interface CitationPanelProps {
  citations: Citation[];
  defaultExpanded?: boolean;
  className?: string;
}

const SOURCE_ICONS: Record<string, typeof FileText> = {
  Qualitative: FileText,
  Database: Database,
  Document: FileText,
  External: Link2,
  default: FileQuestion,
};

function getSourceIcon(sourceType: string) {
  return SOURCE_ICONS[sourceType] || SOURCE_ICONS.default;
}

function formatDate(dateString: string | null): string {
  if (!dateString) return '';
  try {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return dateString;
  }
}

export function CitationPanel({
  citations,
  defaultExpanded = false,
  className,
}: CitationPanelProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  if (citations.length === 0) {
    return null;
  }

  return (
    <div className={cn('space-y-2', className)}>
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
      >
        {isExpanded ? (
          <ChevronUp className="h-4 w-4" />
        ) : (
          <ChevronDown className="h-4 w-4" />
        )}
        <FileText className="h-4 w-4" />
        <span>Citations ({citations.length})</span>
      </button>

      {isExpanded && (
        <div className="space-y-2 pl-6">
          {citations.map((citation, idx) => {
            const Icon = getSourceIcon(citation.source_type);
            return (
              <div
                key={idx}
                className="bg-muted/30 rounded-lg p-3 text-sm border-l-2 border-primary"
              >
                <div className="flex items-start gap-2">
                  <Icon className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className="font-medium">
                        {citation.source_type}
                        {citation.source_name && ` - ${citation.source_name}`}
                      </span>
                      {citation.as_of_date && (
                        <span className="text-xs text-muted-foreground">
                          ({formatDate(citation.as_of_date)})
                        </span>
                      )}
                    </div>
                    {citation.excerpt && (
                      <p className="text-muted-foreground mt-1 italic line-clamp-3">
                        "{citation.excerpt}"
                      </p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
